if (!customElements.get('product-gallery-swiper')) {
  customElements.define(
    'product-gallery-swiper',
    class ProductGallerySwiper extends HTMLElement {
      connectedCallback() {
        // Swiper is fetched lazily (see layout/theme.liquid) so it doesn't
        // block initial page load. Until it's ready, CSS alone keeps a
        // usable scroll-snap gallery visible (see the
        // .product-gallery-swiper--not-initialized rule in
        // component-product-gallery-swiper.css).
        if (typeof Swiper !== 'undefined') {
          this.initSwiper();
        } else {
          this.onSwiperLoaded = this.initSwiper.bind(this);
          document.addEventListener('swiper:loaded', this.onSwiperLoaded, { once: true });
        }
      }

      initSwiper() {
        if (this.mainSwiper) return;

        const thumbsEl = this.querySelector('.product-gallery-swiper__thumbs');
        const mainEl = this.querySelector('.product-gallery-swiper__main');

        // Below 1200px, thumbs sit in a horizontal row under the image (see
        // component-product-gallery-swiper.css); at 1200px+ they become a
        // vertical column to its left. Swiper doesn't support switching
        // `direction` via breakpoints (it needs different internal axis
        // math), so this is set on init from the current viewport and then
        // flipped explicitly via changeDirection() when the breakpoint is
        // crossed - see thumbsVerticalMql below.
        this.thumbsVerticalMql = window.matchMedia('(min-width: 1200px)');

        // fitSlidesToViewport (below) needs this ready before constructing
        // mainSwiper: Swiper fires its own `init` callback synchronously
        // from inside `new Swiper(...)`, i.e. before that call even
        // returns, so anything fitSlidesToViewport reads has to already
        // exist by then. 990px matches wherever .product-gallery-swiper
        // (this element - see the class on the tag in
        // product-media-gallery.liquid) gets a max-height in
        // section-main-product.css; a different, wider breakpoint than
        // thumbsVerticalMql's 1200px, which is only about the thumbnail
        // strip's own layout direction.
        this.heightFitMql = window.matchMedia('(min-width: 990px)');

        this.thumbsSwiper = new Swiper(thumbsEl, {
          direction: this.thumbsVerticalMql.matches ? 'vertical' : 'horizontal',
          slidesPerView: 'auto',
          spaceBetween: 12,
          watchSlidesProgress: true,
        });

        // The thumb-nav arrows drive the main image (not just the thumbnail
        // strip's own scroll position) - clicking them selects the next/prev
        // product image, same as the main swiper's own nav buttons, and
        // brings the newly active thumbnail to the front of the visible
        // strip rather than merely scrolling it into view somewhere.
        // Not rendered at all when there's only one image (see
        // product-media-gallery.liquid) - nothing to slide to.
        const thumbPrevEl = thumbsEl.querySelector('.product-gallery-swiper__thumb-nav--prev');
        const thumbNextEl = thumbsEl.querySelector('.product-gallery-swiper__thumb-nav--next');
        if (thumbPrevEl) thumbPrevEl.addEventListener('click', () => this.goToAdjacentSlide(-1));
        if (thumbNextEl) thumbNextEl.addEventListener('click', () => this.goToAdjacentSlide(1));

        this.mainSwiper = new Swiper(mainEl, {
          // Peek amount below 990px (no viewport-height cap active - see
          // heightFitMql above): a narrower viewport gives the fixed-width
          // thumbnail column relatively less room to spare, so keep more of
          // it for the image (smaller slidesPerView) there. At 990px+, this
          // gets overridden dynamically by fitSlidesToViewport below instead
          // of a fixed peek amount, so the image's own aspect-ratio height
          // (width / aspect-ratio - see .product-media-container in
          // component-product-gallery-swiper.css) fits inside
          // .product-gallery-swiper's max-height (section-main-product.css)
          // instead of being cropped by it.
          slidesPerView: 1.15,
          spaceBetween: 10,
          // loop: true,
          breakpoints: {
            750: {
              spaceBetween: 25,
            },
          },
          watchSlidesProgress: true,
          thumbs: { swiper: this.thumbsSwiper },
          // navigation: {
          //   prevEl: mainEl.querySelector('.slider-button--prev'),
          //   nextEl: mainEl.querySelector('.slider-button--next'),
          // },
          on: {
            init: (swiper) => {
              this.classList.remove('product-gallery-swiper--not-initialized');
              this.updateCounter(swiper);
              this.updateActiveThumbnail(swiper);
              // this.updateThumbNavState(swiper);
              this.playActiveSlide(swiper);
              this.fitSlidesToViewport(swiper);
            },
            slideChange: (swiper) => {
              this.updateCounter(swiper);
              this.updateActiveThumbnail(swiper);
              this.updateThumbNavState(swiper);
              this.playActiveSlide(swiper);
              this.fitSlidesToViewport(swiper);
            },
            // Swiper's own width-driven resize handling (via its internal
            // ResizeObserver on the container) resets slidesPerView back to
            // params/breakpoint defaults on every resize before emitting
            // this event - reapplying here, inside the same pass, is what
            // makes the override stick instead of racing the separate
            // window "resize" listener below (which exists only to catch
            // viewport HEIGHT changes: 70vh depends on height alone, which
            // doesn't affect this container's own width and so never
            // triggers Swiper's own resize handling).
            resize: (swiper) => this.fitSlidesToViewport(swiper),
          },
        });

        const mainPrevEl = mainEl.querySelector('.slider-button--prev');
        const mainNextEl = mainEl.querySelector('.slider-button--next');
        if (mainPrevEl) mainPrevEl.addEventListener('click', () => this.wrapToAdjacentSlide(-1));
        if (mainNextEl) mainNextEl.addEventListener('click', () => this.wrapToAdjacentSlide(1));

        this.resizeDebounceId = null;
        this.onWindowResize = () => {
          window.clearTimeout(this.resizeDebounceId);
          this.resizeDebounceId = window.setTimeout(() => this.fitSlidesToViewport(this.mainSwiper), 150);
        };
        window.addEventListener('resize', this.onWindowResize);

        this.onHeightFitBreakpointChange = () => this.fitSlidesToViewport(this.mainSwiper);
        this.heightFitMql.addEventListener('change', this.onHeightFitBreakpointChange);

        // The thumbs column is display:none below 990px (see
        // component-product-gallery-swiper.css) - tablet now matches mobile's
        // single peek slider with no thumbnail strip - so its Swiper instance
        // measures a zero-size element there. Re-measure only when the
        // breakpoint is actually crossed, rather than watching continuously
        // (a continuous ResizeObserver here previously caused a feedback
        // loop with the main swiper and produced runaway slide widths).
        this.thumbsVisibleMql = window.matchMedia('(min-width: 990px)');
        this.onVisibilityBreakpointChange = () => {
          this.thumbsSwiper.update();
          this.mainSwiper.update();
        };
        this.thumbsVisibleMql.addEventListener('change', this.onVisibilityBreakpointChange);

        this.onDirectionBreakpointChange = (event) => {
          this.thumbsSwiper.changeDirection(event.matches ? 'vertical' : 'horizontal', true);
          this.thumbsSwiper.update();
          this.mainSwiper.update();
        };
        this.thumbsVerticalMql.addEventListener('change', this.onDirectionBreakpointChange);
      }

      // 990px+ only (see heightFitMql above): instead of letting
      // .product-gallery-swiper's max-height (section-main-product.css)
      // clip the active image, shrink or grow how much slide width is
      // visible (slidesPerView) so the image's own aspect-ratio height
      // lands within that cap instead.
      fitSlidesToViewport(swiper) {
        if (!this.heightFitMql.matches) return;

        const activeSlide = swiper.slides[swiper.activeIndex];
        const container = activeSlide && activeSlide.querySelector('.product-media-container');
        if (!container) return;

        const aspectRatio = parseFloat(getComputedStyle(container).getPropertyValue('--aspect-ratio'));
        if (!aspectRatio) return;

        const availableHeight = this.getAvailableHeight();
        if (availableHeight <= 0) return;

        const idealSlideWidth = availableHeight * aspectRatio;
        const idealSlidesPerView = swiper.el.clientWidth / idealSlideWidth;
        // Never less than 1 (a full slide always stays fully visible - this
        // is what makes room, not a shrink-to-fit) and capped at 2 so an
        // unusually wide/landscape image can't balloon into showing several
        // slides' worth of width at once.
        const slidesPerView = Math.min(Math.max(idealSlidesPerView, 1), 2);

        if (Math.abs(swiper.params.slidesPerView - slidesPerView) > 0.01) {
          swiper.params.slidesPerView = slidesPerView;
          swiper.update();
        }
      }

      // .product-gallery-swiper's own max-height (this element - see the
      // class on the tag in product-media-gallery.liquid) is the actual
      // constraint the image needs to fit inside without being cropped -
      // read it live off the element instead of duplicating whatever
      // value/unit it's set to in CSS, so this keeps working if that rule
      // changes independently of this file.
      getAvailableHeight() {
        const maxHeight = parseFloat(getComputedStyle(this).maxHeight);
        if (!(maxHeight > 0)) {
          // Fallback if no max-height rule is set: use the theme's own
          // live-tracked --header-height (sections/header.liquid), which
          // already includes the announcement bar when one is enabled,
          // subtracted from the actual viewport height.
          const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
          return window.innerHeight - headerHeight;
        }

        // >=1200px, thumbs sit to the left, taken out of the flex flow
        // entirely (position: absolute - see component-product-gallery-swiper.css)
        // so they don't share this element's own max-height budget with the
        // main image - the full cap belongs to the image alone.
        if (this.thumbsVerticalMql.matches) return maxHeight;

        // <1200px, thumbs sit in-flow BELOW the image, inside this same
        // max-height'd flex column - the image only gets whatever's left
        // after the thumbnail strip and the gap between them. Without
        // subtracting those, the image gets sized as if it had the whole
        // cap to itself and the leftover (thumbsHeight + gap) worth of it
        // gets clipped off the bottom by .product-gallery-swiper__main's
        // own overflow: hidden.
        const thumbsEl = this.querySelector('.product-gallery-swiper__thumbs');
        const thumbsHeight = thumbsEl ? thumbsEl.getBoundingClientRect().height : 0;
        const gap = parseFloat(getComputedStyle(this).rowGap) || 0;
        return maxHeight - thumbsHeight - gap;
      }

      updateCounter(swiper) {
        const current = this.querySelector('.product-gallery-swiper__counter-current');
        if (current) current.textContent = swiper.activeIndex + 1;
      }

      updateActiveThumbnail(swiper) {
        if (!this.thumbsSwiper) return;
        this.thumbsSwiper.slides.forEach((slide) => {
          const button = slide.querySelector('.thumbnail');
          if (button) button.removeAttribute('aria-current');
        });
        const activeThumb = this.thumbsSwiper.slides[swiper.activeIndex];
        const activeButton = activeThumb && activeThumb.querySelector('.thumbnail');
        if (activeButton) activeButton.setAttribute('aria-current', 'true');

        // Thumbs are display:none below 990px, so Swiper never computes
        // visibleSlidesIndexes there. Skip scrolling a hidden strip.
        const visible = this.thumbsSwiper.visibleSlidesIndexes;
        if (visible && !visible.includes(swiper.activeIndex)) {
          this.thumbsSwiper.slideTo(swiper.activeIndex);
        }
      }

      // goToAdjacentSlide(delta) {
      //   const nextIndex = this.mainSwiper.activeIndex + delta;
      //   if (nextIndex < 0 || nextIndex >= this.mainSwiper.slides.length) return;
      //   this.mainSwiper.slideTo(nextIndex);
      //   // slideChange (fired by slideTo above) already repositions the thumb
      //   // strip if the new active thumb isn't visible at all, but here it
      //   // should always become the front-most visible thumb, not just
      //   // "visible somewhere" - slideTo again to force that.
      //   this.thumbsSwiper.slideTo(nextIndex);
      // }

      goToAdjacentSlide(delta) {
        const total = this.mainSwiper.slides.length;
        const nextIndex = (this.mainSwiper.activeIndex + delta + total) % total;
        this.mainSwiper.slideTo(nextIndex);
        this.thumbsSwiper.slideTo(nextIndex);
      }

      wrapToAdjacentSlide(delta) {
        const swiper = this.mainSwiper;
        if (delta > 0 && swiper.isEnd) {
          swiper.slideTo(0);
        } else if (delta < 0 && swiper.isBeginning) {
          swiper.slideTo(swiper.slides.length - 1);
        } else if (delta > 0) {
          swiper.slideNext();
        } else {
          swiper.slidePrev();
        }
      }

      updateThumbNavState(swiper) {
        const prevEl = this.querySelector('.product-gallery-swiper__thumb-nav--prev');
        const nextEl = this.querySelector('.product-gallery-swiper__thumb-nav--next');
        if (prevEl) prevEl.classList.toggle('swiper-button-disabled', swiper.isBeginning);
        if (nextEl) nextEl.classList.toggle('swiper-button-disabled', swiper.isEnd);
      }

      playActiveSlide(swiper) {
        const activeSlide = swiper.slides[swiper.activeIndex];
        if (!activeSlide) return;
        window.pauseAllMedia();
        const deferredMedia = activeSlide.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      disconnectedCallback() {
        if (this.onSwiperLoaded) {
          document.removeEventListener('swiper:loaded', this.onSwiperLoaded);
        }
        if (this.thumbsVisibleMql) {
          this.thumbsVisibleMql.removeEventListener('change', this.onVisibilityBreakpointChange);
        }
        if (this.thumbsVerticalMql) {
          this.thumbsVerticalMql.removeEventListener('change', this.onDirectionBreakpointChange);
        }
        if (this.heightFitMql) {
          this.heightFitMql.removeEventListener('change', this.onHeightFitBreakpointChange);
        }
        window.removeEventListener('resize', this.onWindowResize);
        window.clearTimeout(this.resizeDebounceId);
        if (this.mainSwiper) {
          this.mainSwiper.destroy(true, true);
          this.mainSwiper = null;
        }
        if (this.thumbsSwiper) {
          this.thumbsSwiper.destroy(true, true);
          this.thumbsSwiper = null;
        }
      }
    }
  );
}
